import "primereact/resources/themes/lara-light-blue/theme.css";
import "primereact/resources/primereact.min.css";
import "primeicons/primeicons.css";
import { PrimeReactProvider } from "primereact/api";
import React, { useState, useEffect, useRef } from "react";
import {
  Routes,
  Route,
  BrowserRouter,
  useNavigate,
  Navigate,
} from "react-router-dom";
import "./App.css";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { io } from "socket.io-client";
import Layout from "./components/Layout";
import { isAdminUser } from "./utils/auth";
import { initOrderSound, playOrderSound, keepScreenAwake } from "./utils/orderSound";
import { refreshPushSubscription } from "./utils/pushNotifications";
import NewOrderAlert from "./components/OrderManagement/NewOrderAlert";
import { isOwnOrderUpdate } from "./utils/orderActions";
import ScrollToTop from "./components/ScrollToTop";

// Pages & Components
import MainDash from "./pages/MainDash/MainDash";
import SettingsHub from "./pages/SettingsHub";
import ProductAdd from "./pages/Product/Add";
import ProductList from "./pages/Product/List";
import ProductBulkUpload from "./pages/Product/BulkUpload";
import ComboList from "./pages/Combo/ComboList";
import ComboAdd from "./pages/Combo/ComboAdd";
import Sidebar from "./components/Sidebar";
import Banner from "./pages/Banner";
import CatSearch from "./pages/category/CatSearch";
import CatAdd from "./pages/category/CatAdd";
import Report from "./pages/Product/Availability";
import SubcatSearch from "./pages/subcat/SubcatSearch";
import SubcatAdd from "./pages/subcat/SubcatAdd";
import Price from "./pages/PriceUpdate";
import Login from "./pages/Auth/Login";
import NewOrderDetails from "./pages/Order/NewOrderDetails";
import OrdersHistory from "./components/OrderManagement/OrdersHistory";
import CustomerReport from "./components/OrderManagement/CustomerReport";
import ProductReport from "./components/OrderManagement/ProductReport";
import Dinein from "./pages/Order/Dine-in";
import Takeaway from "./pages/Takeaway";
import TakeawayHistoryPage from "./pages/Takeaway/History";
import TableBookingPage from "./pages/TableBooking/Tablebooking";
import TableBookingOrderPage from "./pages/TableBooking/OrderPage";
import AddOnsPage from "./pages/AddOns/AddOnsPage";
import AddUser from "./pages/UserManagement";
import QrBookingPage from "./pages/QRBooking/QrOrder";
import Setting from "./pages/Setting";
import Configure from "./pages/Configure";
import UserFeedback from "./pages/userfeedback/userfeedback";
import { setLogoutCallback } from "./utils/api";
import ChangePassword from "./pages/Auth/ChangePassword";
import { RestaurantDetailsProvider } from "./context/RestaurantDetailsContext";
import socket, { reconnectSocket } from "./socket";
import { vibrateWaiterAlert } from "./utils/vibration";
import OtherAppsSale from "./pages/OtherAppsSale";
import OtherAppSaleHistory from "./pages/OtherAppsSale/OtherAppSaleHistory";


function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(
    !!localStorage.getItem("token"),
  );
  const [newOrders, setNewOrders] = useState([]);
  const newOrdersRef = useRef(newOrders);
  newOrdersRef.current = newOrders;
  const username = localStorage.getItem("username");
  const navigate = useNavigate();

  //  Logout callback setup
  useEffect(() => {
    setLogoutCallback(() => {
      setIsAuthenticated(false);
      navigate("/login");
    });
  }, [navigate]);

  useEffect(() => {
    if (!isAuthenticated) {
      // console.log("⛔ Socket not started: user not authenticated");
      return;
    }

    const role = localStorage.getItem("role");
    const userId = localStorage.getItem("userId");

    // console.log("👤 Socket init check → role:", role, "userId:", userId);

    if (role !== "waiter" || !userId) {
      // console.log("⛔ Not a waiter or userId missing → skipping waiter socket");
      return;
    }

    const onConnect = () => {
      // console.log("🟢 Waiter socket connected:", socket.id);
      // console.log("➡️ Joining waiter room:", `waiter:${userId}`);
      socket.emit("join-waiter", { userId });
    };

    const onWaiterCalled = (data) => {
      // console.log("🔔 waiter-called event received:", data);

      toast.warning(`🔔 Table ${data.tableNumber} is calling you`, {
        position: "top-right",
        autoClose: 5000,
      });

      // 📳 Vibrate Phone
      vibrateWaiterAlert();
    };

    socket.on("connect", onConnect);
    socket.on("waiter-called", onWaiterCalled);

    return () => {
      socket.off("connect", onConnect);
      socket.off("waiter-called", onWaiterCalled);
    };
  }, [isAuthenticated]);

  const getDefaultRoute = () => {
    const role = localStorage.getItem("role");
    if (role === "waiter") {
      return "/table-booking";
    }
    if (role === "fingliderAdmin") {
      return "/configure";
    }
    return "/dashboard";
  };

  //  Handle successful login
  const handleLoginSuccess = async (token) => {
    // console.log("✅ handleLoginSuccess triggered");

    setIsAuthenticated(true);
    localStorage.setItem("token", token);
    reconnectSocket(); // reconnect as this staff user to get live order updates

    refreshPushSubscription(); // no prompt; the Enable button in Restaurant Settings asks for permission

    navigate(getDefaultRoute(), { replace: true });
  };



  useEffect(() => {
    initOrderSound();
  }, []);

  useEffect(() => {
    if (!isAuthenticated || !isAdminUser()) return;
    return keepScreenAwake();
  }, [isAuthenticated]);

  useEffect(() => {
    if (!isAuthenticated || !isAdminUser()) return;

    const onNewOrder = (order) => {
      playOrderSound();

      // Accept/reject popup is only for online orders; others just get a toast
      if ((order.orderType || "online").toLowerCase() !== "online") {
        toast.info(`🚨 New Order Arrived! Order ID: ${order.orderId}`, {
          position: "top-right",
          autoClose: 5000,
        });
        return;
      }

      // Queue it; the one-tap accept/reject popup shows the oldest first
      setNewOrders((prev) =>
        prev.some((o) => o.orderId === order.orderId) ? prev : [...prev, order],
      );
    };

    // Accepted/rejected on another screen → close its popup here too
    const onStatusChanged = ({ orderId, status, by }) => {
      if (status === "pending") return;
      if (
        newOrdersRef.current.some((o) => o.orderId === orderId) &&
        !isOwnOrderUpdate(orderId)
      ) {
        toast.info(
          `Order #${orderId} was ${status === "reject" ? "rejected" : "accepted"}${by ? ` by ${by}` : ""}`,
          { position: "top-right", autoClose: 4000 },
        );
      }
      setNewOrders((prev) => prev.filter((o) => o.orderId !== orderId));
    };

    socket.on("new-order", onNewOrder);
    socket.on("order-status-changed", onStatusChanged);

    return () => {
      socket.off("new-order", onNewOrder);
      socket.off("order-status-changed", onStatusChanged);
    };
  }, [isAuthenticated]);

  // Remove by id: the order may already have been removed by a
  // status-changed event, so never just drop the first item.
  const dismissNewOrder = (orderId) =>
    setNewOrders((prev) => prev.filter((o) => o.orderId !== orderId));

  const openNewOrderInFull = (orderId) => {
    dismissNewOrder(orderId);
    if (orderId) navigate(`/orderdetails?open=${encodeURIComponent(orderId)}`);
  };

  // useEffect(() => {
  //   if (isAuthenticated && isAdminUser()) {
  //     subscribeToPush();
  //   }
  // }, [isAuthenticated]);

  if (!isAuthenticated) {
    return (
      <Routes>
        <Route
          path="/"
          element={<Login onLoginSuccess={handleLoginSuccess} />}
        />
        <Route path="*" element={<Navigate to="/" />} />
      </Routes>
    );
  }

  if (localStorage.getItem("mustChangePassword") === "true") {
    return (
      <Routes>
        <Route path="/change-password" element={<ChangePassword />} />
        <Route path="*" element={<Navigate to="/change-password" replace />} />
      </Routes>
    );
  }

  // Determine default route based on user role


  return (
    <div className="App">
      <NewOrderAlert
        order={isAuthenticated ? newOrders[0] : null}
        pendingCount={newOrders.length - 1}
        onDone={dismissNewOrder}
        onOpenFull={openNewOrderInFull}
      />
      {/* <div className="AppGlass">
        {isAuthenticated && <Sidebar setIsAuthenticated={setIsAuthenticated} />} */}
      <Routes>
        <Route path="/" element={<Navigate to={getDefaultRoute()} />} />
        {/* <Route path="/dashboard" element={<MainDash username={username} />} />
          <Route path="/product" element={<ProductList />} />
          <Route path="/product/productdetail/:id" element={<ProductAdd />} />
          <Route path="/product/add" element={<ProductAdd />} />
          <Route path="/price-update" element={<Price />} />
          <Route path="/categories" element={<CatSearch />} />
          <Route path="/add-category" element={<CatAdd />} />
          <Route path="/subcategories" element={<SubcatSearch />} />
          <Route path="/add-subcategory" element={<SubcatAdd />} />
          <Route path="/feedback" element={<Feedback />} />
          <Route path="/dining" element={<Dinein />} />
          <Route path="/banner" element={<Banner />} />
          <Route path="/reports" element={<Report />} />
          <Route path="/change-password" element={<ChangePassword />} />
          <Route path="/orderdetails" element={<NewOrderDetails />} />
          <Route path="/orders-history" element={<OrdersHistory />} /> */}
        {/* Single shared Layout — never remounts on navigation */}
        <Route element={<Layout setIsAuthenticated={setIsAuthenticated} hideHeader={true} />}>
          <Route
            path="/dashboard"
            element={
              isAdminUser() ? (
                <MainDash username={username} />
              ) : localStorage.getItem("role") === "fingliderAdmin" ? (
                <Navigate to="/configure" replace />
              ) : (
                <Navigate to="/table-booking" replace />
              )
            }
          />
          <Route path="/product" element={<ProductList />} />
          <Route path="/product/add" element={<ProductAdd />} />
          <Route path="/product/productdetail/:id" element={<ProductAdd />} />
          <Route path="/product/bulk-upload" element={<ProductBulkUpload />} />
          <Route path="/combos" element={<ComboList />} />
          <Route path="/combos/add" element={<ComboAdd />} />
          <Route path="/combos/edit/:id" element={<ComboAdd />} />
          <Route path="/addons" element={<AddOnsPage />} />
          <Route path="/price-update" element={<Price />} />
          <Route path="/categories" element={<CatSearch />} />
          <Route path="/add-category" element={<CatAdd />} />
          <Route path="/subcategories" element={<SubcatSearch />} />
          <Route path="/add-subcategory" element={<SubcatAdd />} />
          <Route path="/feedback" element={<UserFeedback />} />
          <Route path="/orderdetails" element={<NewOrderDetails />} />
          <Route path="/orders-history" element={<OrdersHistory key="orders-history" />} />
          <Route path="/customer-report" element={<CustomerReport />} />
          <Route path="/product-report" element={<ProductReport />} />
          <Route path="/report-other-app-sale" element={<OrdersHistory key="other-apps-sale" defaultToSummary={true} isSummaryPage={true} />} />
          <Route path="/dining" element={<Dinein />} />
          <Route path="/banner" element={<Banner />} />
          <Route path="/other-apps-sale" element={<OtherAppsSale />} />
          <Route path="/other-apps-sale/app/:appId" element={<OtherAppSaleHistory />} />
          <Route path="/setting" element={<Setting />} />
          <Route path="/configure" element={<Configure />} />
          <Route path="/settings" element={<SettingsHub />} />
          <Route path="/reports" element={<Report />} />
          <Route path="/takeaway" element={<Takeaway />} />
          <Route path="/takeaway/history" element={<TakeawayHistoryPage />} />
          <Route path="/table-booking" element={<TableBookingPage />} />
          <Route path="/table-booking/order/:allocationId" element={<TableBookingOrderPage />} />
          <Route path="/addusers" element={<AddUser />} />
          <Route path="/qrordering" element={<QrBookingPage />} />
        </Route>
      </Routes>
      {/* </div> */}
    </div>
  );
}

function AppWrapper() {
  return (
    <PrimeReactProvider>
      {/* Single app-wide toast container — the only ToastContainer in the app.
          Mounted here so it stays alive across every route (login,
          force-password-change, and the authenticated app) and never
          unmounts on navigation. */}
      <ToastContainer position="top-center" autoClose={3000} />
      <BrowserRouter>
        <ScrollToTop />
        <RestaurantDetailsProvider>
          <App />
        </RestaurantDetailsProvider>
      </BrowserRouter>
    </PrimeReactProvider>
  );
}

export default AppWrapper;
